import { useRef } from "react";
import { signal } from "@preact/signals-react";

import { classNames } from "tools/ui";
import StdButton from "components/Buttons/StdButton";
import { BackspaceIcon, XCircleIcon } from "components/Icons";

const NUMBERS_DEFAULT = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];
const numberCode = signal("");

const STYLE_BUTTONS =
  "w-full rounded-md text-white p-2 text-xl lg:text-3xl xl:p-3 bg-gray-800 shadow-md active:bg-gray-700 hover:shadow-xl active:scale-95 active:text-white transition-all duration-100";

const InputNumber = ({ value, onChange, onKeyDown, limit }) => (
  <input
    type="number"
    className="w-full bg-white px-2 py-2 xl:py-3 my-2 md:text-4xl placeholder:text-2xl tablet:placeholder:text-xl text-center rounded-md shadow-lg border border-zinc-400"
    placeholder="Digita tu código"
    value={value}
    maxLength={limit}
    onChange={onChange}
    onKeyDown={onKeyDown}
  />
);

const OnScreenKeyboard = ({
  numbers = NUMBERS_DEFAULT,
  onClick,
  onCancel,
  limitNumber = 4,
  colorButtons = "",
}) => {
  const inputRef = useRef(null);

  const addDigit = (digit) => {
    if (!Number.isInteger(digit)) return;

    if (numberCode.value.length < limitNumber) {
      numberCode.value += digit.toString();
    }

    inputRef.current?.focus();
  };

  const deleteDigit = () => {
    numberCode.value = numberCode.value.slice(0, -1);
    inputRef.current?.focus();
  };

  const clearCode = () => {
    numberCode.value = "";
    inputRef.current?.focus();
  };

  const handleInputChange = (event) => {
    const value = event.target.value.replace(/\D/g, ""); // solo números
    numberCode.value = value.slice(0, limitNumber);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && numberCode.value) {
      confirmCode();
    }
  };

  const confirmCode = () => {
    if (onClick) {
      onClick(numberCode.value);
      clearCode();
    }
  };

  function handleClose() {
    clearCode();
    onCancel();
  }

  return (
    <div className="w-full max-w-2xl mx-auto lg:px-6 rounded-md">
      <InputNumber
        ref={inputRef}
        value={numberCode.value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        limit={limitNumber}
      />

      <div className="grid grid-cols-3 w-full gap-3 lg:gap-6 mt-2">
        {numbers.map((number) => (
          <button
            key={number}
            type="button"
            className={classNames(STYLE_BUTTONS, colorButtons)}
            onClick={() => addDigit(number)}
          >
            <span className="flex mt-2 justify-center">{number}</span>
          </button>
        ))}

        <button
          type="button"
          className={classNames(STYLE_BUTTONS, "!bg-gray-200")}
          onClick={deleteDigit}
        >
          <BackspaceIcon className="xl:w-14 w-8 mx-auto text-gray-800 fill-current" />
        </button>

        <button
          type="button"
          className={classNames(STYLE_BUTTONS, "bg-red-200")}
          onClick={clearCode}
        >
          <XCircleIcon className="xl:w-14 w-8 mx-auto text-red-900 fill-current" />
        </button>
      </div>

      <div className="flex gap-2 ">
        <StdButton
          style="from-rose-500 to-rose-500 mx-auto mt-4 md:mt-8 !text-white !md:text-2xl uppercase"
          size={"md:w-full md:max-w-md lg:h-16"}
          content="button.cancel"
          onClick={handleClose}
          color="rose"
        />
        <StdButton
          style="!from-[#00726e] !to-[#00726e] mx-auto mt-4 md:mt-8 !text-white !md:text-2xl uppercase"
          size={"md:w-full md:max-w-md h-12 lg:h-16"}
          onClick={confirmCode}
          content="button.confirm"
          color="bluePresik"
        />
      </div>
    </div>
  );
};

export default OnScreenKeyboard;
